import { Extension } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import { REFERENCE_CANDIDATE_PATTERNS } from '../../../sidebar/references/referenceCandidatePatterns';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    referenceCandidateHighlight: {
      setReferenceCandidateHighlight: (enabled: boolean) => ReturnType;
    };
  }
}

export type ReferenceCandidateTypes = 'figure' | 'table' | 'heading' | 'guide' | 'standard';

export interface ReferenceCandidateHighlightOptions {
  patterns: {
    figures: readonly RegExp[];
    tables: readonly RegExp[];
    headings: readonly RegExp[];
    guides: readonly RegExp[];
    standards: readonly RegExp[];
  };
  enabled: boolean;
  onCandidateClick?: (text: string, type: ReferenceCandidateTypes) => void;
}

export const ReferenceCandidateHighlight = Extension.create<ReferenceCandidateHighlightOptions>({
  name: 'referenceCandidateHighlight',
  addOptions() {
    return {
      patterns: REFERENCE_CANDIDATE_PATTERNS,
      enabled: false,
      onCandidateClick: undefined,
    };
  },

  addCommands() {
    return {
      setReferenceCandidateHighlight:
        (enabled: boolean) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta('referenceCandidateHighlightToggle', enabled);
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const extension = this;
    return [
      new Plugin({
        key: new PluginKey('referenceCandidateHighlight'),
        state: {
          init: (_, { doc }) => {
            return findCandidates(doc, extension.options.patterns, extension.options.enabled);
          },
          apply: (transaction, oldState) => {
            const toggleMeta = transaction.getMeta('referenceCandidateHighlightToggle');
            if (toggleMeta !== undefined) {
              extension.options.enabled = toggleMeta;
              return findCandidates(transaction.doc, extension.options.patterns, extension.options.enabled);
            }
            if (transaction.docChanged) {
              return findCandidates(transaction.doc, extension.options.patterns, extension.options.enabled);
            }
            return oldState;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
          handleClick(view, pos, event) {
            const target = event.target as HTMLElement;
            const candidateTypes = [
              { className: 'reference-candidate-figure', type: 'figure' as const },
              { className: 'reference-candidate-table', type: 'table' as const },
              { className: 'reference-candidate-heading', type: 'heading' as const },
              { className: 'reference-candidate-guide', type: 'guide' as const },
              { className: 'reference-candidate-standard', type: 'standard' as const },
            ];

            for (const { className, type } of candidateTypes) {
              if (target.classList.contains(className)) {
                const decorations = this.getState(view.state);
                if (decorations) {
                  const found = decorations.find(pos, pos);
                  if (found.length > 0) {
                    const decoration = found[0];
                    const { from, to } = decoration;
                    const text = view.state.doc.textBetween(from, to);

                    // Call the callback if provided
                    if (extension.options.onCandidateClick) {
                      extension.options.onCandidateClick(text, type);
                    }

                    // Select the text
                    const selection = TextSelection.create(view.state.doc, from, to);
                    view.dispatch(view.state.tr.setSelection(selection));
                    return true;
                  }
                }
              }
            }
            return false;
          },
        },
      }),
    ];
  },
});

function findCandidates(doc: any, patterns: ReferenceCandidateHighlightOptions['patterns'], enabled: boolean): DecorationSet {
  if (!enabled) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];

  doc.descendants((node: any, pos: number) => {
    if (node.isText && node.text) {
      const text = node.text;

      // Check all pattern types
      const allPatterns = [
        ...patterns.figures.map((p) => ({ pattern: p, className: 'reference-candidate-figure' })),
        ...patterns.tables.map((p) => ({ pattern: p, className: 'reference-candidate-table' })),
        ...patterns.headings.map((p) => ({ pattern: p, className: 'reference-candidate-heading' })),
        ...patterns.guides.map((p) => ({ pattern: p, className: 'reference-candidate-guide' })),
        ...patterns.standards.map((p) => ({ pattern: p, className: 'reference-candidate-standard' })),
      ];

      allPatterns.forEach(({ pattern, className }) => {
        const regex = new RegExp(pattern.source, pattern.flags);
        let match;

        while ((match = regex.exec(text)) !== null) {
          const from = pos + match.index;
          const to = from + match[0].length;

          decorations.push(
            Decoration.inline(from, to, {
              class: className,
              'data-reference-candidate': match[0],
            }),
          );
        }
      });
    }
  });

  return DecorationSet.create(doc, decorations);
}
